import { IConfigSeller } from '@/models/configSeller'
import { ITradeinContext } from '@/models/ITradein'
import Checklist from '@/pages/backoffice/TradeInPage/components/Checklist'
import { TradeinContext } from '@/pages/backoffice/TradeInPage/context/TradeinContext'
import { useTradeinContext } from '@/pages/backoffice/TradeInPage/hooks/useTradeinContext'
import { webApiService } from '@/services'
import { Spinner } from '@nextui-org/react'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

// Esta ruta es pública (/:slug/checklist/:id) y no arrastra la sesión del
// backoffice, por lo que getById se llama con credenciales de servicio fijas.
const CHECKLIST_EMAIL = 'rromero@reuse.mx'
// El id_role del usuario de servicio cambia por país (admin: MX = 19, CL = 33).
const CHECKLIST_ID_ROLE = Number(import.meta.env.VITE_PERFIL_ADMIN)

// Estado inicial mínimo: Checklist sólo se monta una vez que el tradein ya se
// cargó (ver isLoading), así que aquí basta con tradein.id = 0 y colecciones vacías.
const initialCtx = {
  tradein: { id: 0 },
  questions: [],
  answers: [],
  entrys: [],
  formAnswers: [],
  statusHistory: [],
  files: [],
  openModal: false,
  loading: false,
  reload: false
} as unknown as ITradeinContext

// Provider propio de la página del checklist. Reutiliza el TradeinContext
// compartido (para que Checklist siga funcionando con useTradeinContext) pero
// carga el tradein por el :id de la URL usando credenciales de servicio, sin
// depender de la sesión del usuario. NO usa el TradeinProvider del backoffice.
const ChecklistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ctx, setCtx] = useState<ITradeinContext>(initialCtx)
  const { id } = useParams()

  const saveCtx = (_ctx: ITradeinContext) => setCtx(_ctx)

  useEffect(() => {
    if (!id) return
    setCtx((prev) => ({ ...prev, loading: true }))
    Promise.all([
      webApiService.getById({ id_tradein: String(id), email: CHECKLIST_EMAIL, id_role: CHECKLIST_ID_ROLE }),
      webApiService.getChecklistQuestions(),
      webApiService.getEntrysById(String(id)),
      webApiService.getTradeinConfig().then((res) => res.data[0]),
      webApiService.getAllAnswers(String(id)).then((res) => res.data)
    ]).then((res) => {
      setCtx((prev) => ({
        ...prev,
        tradein: res[0].data[0],
        loading: false,
        questions: res[1].data,
        entrys: res[2].data,
        reload: false,
        displayContract: res[3].display_contract,
        formAnswers: res[4]
      }))
    }).catch((error) => {
      setCtx((prev) => ({ ...prev, loading: false, reload: false }))
      console.log('Error loading checklist data:', error)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ctx.reload])

  return <TradeinContext.Provider value={{ ctx, saveCtx }}>{children}</TradeinContext.Provider>
}

// Cabecera con logos, reutilizando el mismo look de ContractPage.
const Header = ({ logo }: { logo: string }) => (
  <div className='grid grid-cols-2 max-w-[1024px] pb-2 mx-auto'>
    <div className='flex items-center'><img src={logo} className='h-14' /></div>
    <div className='flex items-center'>
      <img src={`https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/assets/logo-reuse.png`} className='h-16 ms-auto' />
    </div>
  </div>
)

const ChecklistContent = () => {
  const { ctx } = useTradeinContext()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)

  // Mostramos el spinner hasta que el tradein de la URL esté realmente cargado.
  // Checklist accede a ctx.tradein.sbu/brand/etc., así que montarlo antes de que
  // llegue el objeto (ctx.tradein aún undefined) revienta.
  const isLoading = ctx.loading || !ctx.tradein?.id

  return (
    <div className='bg-white w-full min-h-screen'>
      <div className='container mx-auto p-1 md:py-11'>
        <Header logo={configSeller.config.logo} />
        <div className='max-w-[1024px] mx-auto'>
          {isLoading
            ? (
              <div className='bg-white border-black border-1 p-11 text-center'>
                <Spinner />
              </div>
              )
            : <Checklist autoOpen />}
        </div>
      </div>
    </div>
  )
}

// Página standalone del checklist, abierta en ventana nueva con la ruta
// /:slug/checklist/:id (pública, similar a /:slug/contract/:loginCode).
// Carga el tradein por el :id de la URL y reutiliza el componente Checklist.
const ChecklistPage = () => (
  <ChecklistProvider>
    <ChecklistContent />
  </ChecklistProvider>
)

export default ChecklistPage
